import React from 'react';
import { Card, CardContent } from '../ui';
import { Server, Search, Zap, Trash2, Edit, Shield } from 'lucide-react';

interface ClientCardProps {
  id: string;
  name: string;
  description?: string;
  totalServers: number;
  totalQueries: number;
  totalExecutions: number;
  createdAt: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onManagePermissions?: (id: string) => void;
}

export const ClientCard: React.FC<ClientCardProps> = ({
  id,
  name,
  description,
  totalServers,
  totalQueries,
  totalExecutions,
  createdAt,
  onEdit,
  onDelete,
  onManagePermissions
}) => {
  return (
    <Card hover>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
            {description && (
              <p className="text-sm text-gray-600 mt-1">{description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(id)}
              className="text-gray-400 hover:text-blue-600 transition-colors"
            >
              <Edit size={18} />
            </button>
            <button
              onClick={() => onDelete(id)}
              className="text-gray-400 hover:text-red-600 transition-colors"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
              <Server size={16} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalServers}</p>
            <p className="text-xs text-gray-500">Servers</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
              <Search size={16} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalQueries}</p>
            <p className="text-xs text-gray-500">Queries</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 text-gray-600 mb-1">
              <Zap size={16} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{totalExecutions}</p>
            <p className="text-xs text-gray-500">Executions</p>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Created {new Date(createdAt).toLocaleDateString()}
            </p>
            {onManagePermissions && (
              <button
                onClick={() => onManagePermissions(id)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 border-2 border-blue-700 rounded-lg shadow-sm hover:shadow-md transition-all"
                title="Manage granular permissions for tools, prompts, and resources"
              >
                <Shield size={16} />
                Manage Permissions
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
